import React from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { Edit2, Copy, Clock, GripVertical, Trash2 } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { JiraBadge, useProjectJiraUrl } from '../JiraBadge';
import { RichTextContent } from '../ui/RichTextEditor';
import {
  Task,
  TaskAreaLabels,
  TaskPriorityLabels,
  TaskStatusLabels,
  TaskPriorityColors,
  TaskStatusColors,
  TaskAreaColors
} from '../../types/task';
import { formatTaskDate } from '../../utils/dateUtils';

interface DraggableTaskCardProps {
  task: Task;
  index: number;
  onEdit: (task: Task) => void;
  onDuplicate: (task: Task) => void;
  onDelete?: (task: Task) => void;
  isDragDisabled?: boolean;
  showSelection?: boolean;
  isSelected?: boolean;
  onSelect?: (taskId: string) => void;
}

// Shared component for rendering task card content
export const TaskCardContent: React.FC<{
  task: Task;
  onEdit: (task: Task) => void;
  onDuplicate: (task: Task) => void;
  onDelete?: (task: Task) => void;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  isDragDisabled?: boolean;
  showSelection?: boolean;
  isSelected?: boolean;
  onSelect?: (taskId: string) => void;
}> = ({ task, onEdit, onDuplicate, onDelete, provided, snapshot, isDragDisabled = false, showSelection = false, isSelected = false, onSelect }) => {
  const jiraUrl = useProjectJiraUrl(task.projectId);
  return (
    <Card 
      variant="glass" 
      className={`
        group w-full task-card
        ${snapshot.isDragging 
          ? 'shadow-xl border-blue-500/50 bg-blue-500/20 backdrop-blur-sm' 
          : 'hover:shadow-lg hover:shadow-white/5 hover:border-white/20'
        }
      `}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          <div
            {...provided.dragHandleProps}
            className={`
              flex-shrink-0 p-1 rounded
              hover:bg-white/10 transition-colors
              ${isDragDisabled
                ? 'opacity-30 cursor-not-allowed'
                : snapshot.isDragging
                  ? 'cursor-grabbing'
                  : 'cursor-grab'
              }
            `}
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4 text-foreground-tertiary" />
          </div>

          {/* Selection Checkbox */}
          {showSelection && onSelect && (
            <div className="flex-shrink-0 flex items-center h-6">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onSelect(task._id)}
                className="h-4 w-4 border-white/50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:text-white"
              />
            </div>
          )}

          {/* Task Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h4 className="font-medium text-foreground-primary text-sm leading-tight pt-0.5">
                {task.name}
              </h4>
              
              {/* Action buttons */}
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                  className="h-6 w-6 p-0 text-foreground-tertiary hover:text-foreground-primary"
                  title="Editar tarefa"
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(task);
                  }}
                  className="h-6 w-6 p-0 text-foreground-tertiary hover:text-foreground-primary"
                  title="Duplicar tarefa"
                >
                  <Copy className="w-3 h-3" />
                </Button>
                {onDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(task);
                    }}
                    className="h-6 w-6 p-0 text-foreground-tertiary hover:text-red-400"
                    title="Excluir tarefa"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>

            {task.description && (
              <div className="text-xs text-foreground-tertiary mb-2 line-clamp-3">
                <RichTextContent content={task.description} className="text-xs" />
              </div>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-1 mb-2">
              <Badge className={`${TaskAreaColors[task.area]} text-xs px-1.5 py-0.5`}>
                {TaskAreaLabels[task.area]}
              </Badge>
              <Badge className={`${TaskPriorityColors[task.prioridade]} text-xs px-1.5 py-0.5`}>
                {TaskPriorityLabels[task.prioridade]}
              </Badge>
              <Badge className={`${TaskStatusColors[task.status]} text-xs px-1.5 py-0.5`}>
                {TaskStatusLabels[task.status]}
              </Badge>
              <JiraBadge 
                task={task} 
                jiraUrl={jiraUrl}
                size="sm"
                showLabel={false}
                className="text-xs"
              />
            </div>

            {/* Footer info */}
            <div className="flex items-center justify-between text-xs text-foreground-tertiary">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {task.horasEstimadas}h
              </div>
              <div>
                {formatTaskDate(task.createdAt)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const DraggableTaskCard: React.FC<DraggableTaskCardProps> = ({
  task,
  index,
  onEdit,
  onDuplicate,
  onDelete,
  isDragDisabled = false,
  showSelection = false,
  isSelected = false,
  onSelect
}) => {
  return (
    <Draggable draggableId={task._id} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="mb-2 w-full"
        >
          <TaskCardContent
            task={task}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            provided={provided}
            snapshot={snapshot}
            isDragDisabled={isDragDisabled}
            showSelection={showSelection}
            isSelected={isSelected}
            onSelect={onSelect}
          />
        </div>
      )}
    </Draggable>
  );
};

// Function to create renderClone for Droppable
export const createRenderTaskClone = (
  tasks: Task[],
  onEdit: (task: Task) => void,
  onDuplicate: (task: Task) => void,
  onDelete?: (task: Task) => void
) => {
  return (provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: any) => {
    const task = tasks.find(t => t._id === rubric.draggableId);
    if (!task) return null;

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className="mb-2 w-80"
        style={{
          ...provided.draggableProps.style,
          width: '320px'
        }}
      >
        <TaskCardContent
          task={task}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          provided={provided}
          snapshot={snapshot}
          isDragDisabled={false}
        />
      </div>
    );
  };
};